import React, { useMemo } from 'react';
import {
  CheckIcon,
  XMarkIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { useData } from '../../context/DataContext';
import { formatCurrency } from '../../utils/formatters';
import dayjs from 'dayjs';

const ProposedContributions = () => {
  const {
    proposedTransactions,
    portfolios,
    transactions,
    confirmProposedTransaction,
    rejectProposedTransaction,
    settings,
  } = useData();

  // Compute pending proposals from portfolio settings
  const pending = useMemo(() => {
    const today = dayjs();
    const proposals = [];

    portfolios.forEach(portfolio => {
      if (!portfolio.contributionDay || !portfolio.monthlyContribution || portfolio.monthlyContribution <= 0) return;

      for (let monthsBack = 0; monthsBack <= 3; monthsBack++) {
        const targetMonth = today.subtract(monthsBack, 'month');
        const monthKey = targetMonth.format('YYYY-MM');

        const daysInMonth = targetMonth.daysInMonth();
        const contributionDay = Math.min(portfolio.contributionDay, daysInMonth);
        const contributionDate = targetMonth.date(contributionDay);

        if (contributionDate.isAfter(today)) continue;

        const alreadyActed = proposedTransactions.find(
          p => p.portfolioId === portfolio.id && p.monthKey === monthKey
        );
        if (alreadyActed) continue;

        const existingTransaction = transactions.find(
          t => t.portfolioId === portfolio.id &&
               t.type === 'contribution' &&
               dayjs(t.date).format('YYYY-MM') === monthKey
        );
        if (existingTransaction) continue;

        proposals.push({
          id: `${portfolio.id}-${monthKey}`,
          portfolioId: portfolio.id,
          type: 'contribution',
          amount: portfolio.monthlyContribution,
          date: contributionDate.format('YYYY-MM-DD'),
          potAllocations: portfolio.contributionSplits || [],
          description: `Monthly contribution - ${targetMonth.format('MMM YYYY')}`,
          monthKey,
        });
      }
    });

    return proposals;
  }, [portfolios, proposedTransactions, transactions]);

  if (pending.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-3">
        <div className="flex items-center gap-1.5 mb-2">
          <ClockIcon className="w-4 h-4 text-gray-300" />
          <h3 className="text-xs font-semibold text-gray-900">Pending Contributions</h3>
        </div>
        <p className="text-[11px] text-gray-400">No contributions due</p>
      </div>
    );
  }

  const handleConfirm = (proposal) => {
    confirmProposedTransaction(proposal);
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-3">
      <div className="flex items-center gap-1.5 mb-2">
        <ClockIcon className="w-4 h-4 text-amber-500" />
        <h3 className="text-xs font-semibold text-gray-900">Pending</h3>
        <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-medium">
          {pending.length}
        </span>
      </div>

      <div className="space-y-1.5 max-h-64 overflow-y-auto">
        {pending.map(proposal => {
          const portfolio = portfolios.find(p => p.id === proposal.portfolioId);
          const portfolioName = portfolio?.name || 'Unknown Account';

          return (
            <div key={proposal.id} className="border border-gray-100 rounded-lg p-2">
              <div className="flex items-center justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs font-medium text-gray-900 truncate">{portfolioName}</span>
                    <span className="text-[10px] text-gray-400">{dayjs(proposal.date).format('MMM D')}</span>
                  </div>
                  <div className="flex items-center gap-1 mt-0.5">
                    <span className="text-sm font-bold text-gray-900">
                      {formatCurrency(proposal.amount, portfolio?.currency || settings.displayCurrency, 0)}
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    onClick={() => handleConfirm(proposal)}
                    className="p-1 text-white bg-green-600 hover:bg-green-700 rounded transition-colors"
                    title="Confirm"
                  >
                    <CheckIcon className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => rejectProposedTransaction(proposal)}
                    className="p-1 text-red-500 hover:bg-red-50 rounded transition-colors"
                    title="Skip"
                  >
                    <XMarkIcon className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ProposedContributions;
